import { useState, useEffect } from "react";

import PropTypes from "prop-types";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskUtil from "utils/MaskUtil";

// Função para aplicar máscara de horário
const aplicarMascaraHorario = (valor) => {
  // Remove tudo que não é número
  const numeros = valor.replace(/\D/g, '');
  
  // Limita a 4 dígitos
  const limitado = numeros.slice(0, 4);
  
  // Se tem 3 dígitos, assume que é HH:M
  if (limitado.length === 3) {
    const horas = limitado.slice(0, 2);
    const minuto = limitado.slice(2);
    
    const horasValidas = Math.min(parseInt(horas, 10) || 0, 23).toString().padStart(2, '0');
    return `${horasValidas}:${minuto}`;
  }
  
  // Aplica a máscara HH:MM
  if (limitado.length >= 2) {
    const horas = limitado.slice(0, 2);
    const minutos = limitado.slice(2);
    
    // Valida horas (0-23)
    const horasValidas = Math.min(parseInt(horas, 10) || 0, 23).toString().padStart(2, '0');
    
    // Se tem minutos digitados, valida e formata
    if (minutos.length > 0) {
      const minutosValidos = Math.min(parseInt(minutos, 10) || 0, 59).toString().padStart(2, '0');
      return `${horasValidas}:${minutosValidos}`;
    }
    
    // Se não tem minutos, retorna apenas as horas com ":"
    return `${horasValidas}:`;
  }
  
  return limitado;
};

const EscalaFuncionario = ({ id }) => {
  const { setLoading, loading } = useLoading();
  const auth = useAuth();
  const notify = useNotification();
  const Requicicao = new Api();

  const [mesAno, setMesAno] = useState(null);
  const [tipoEscala, setTipoEscala] = useState(null);
  const [diasTrabalho, setDiasTrabalho] = useState([]);
  const [horarioInicio, setHorarioInicio] = useState("");
  const [horarioFim, setHorarioFim] = useState("");

  const [Lista, setLista] = useState([]);
  const [errors, setErrors] = useState({});

  const tipoEscalaOptions = [
    { label: "5x2", value: "5x2" },
    { label: "6x1", value: "6x1" }
  ];

  const diasSemanaOptions = [
    { label: "Domingo", value: "Domingo" },
    { label: "Segunda", value: "Segunda" },
    { label: "Terça", value: "Terça" },
    { label: "Quarta", value: "Quarta" },
    { label: "Quinta", value: "Quinta" },
    { label: "Sexta", value: "Sexta" },
    { label: "Sábado", value: "Sábado" }
  ];

  const MaxDiasTrabalho = () => {
    let maxDiasTrabalho = 0;
    switch (tipoEscala) {
      case "5x2":
        maxDiasTrabalho = 5;
        break;
      case "6x1":
        maxDiasTrabalho = 6;
        break;
      default:
        maxDiasTrabalho = 0;
    }
    return maxDiasTrabalho;
  };

  const HandleDiasTrabalhoChange = (e) => {
    if (e.value.length <= MaxDiasTrabalho()) {
      setDiasTrabalho(e.value);
    } else {
      notify({
        type: "aviso",
        message: `A escala ${tipoEscala} permite no máximo ${MaxDiasTrabalho()} dias de trabalho.`
      });
    }
  };

  const StateDataPage = async () => {
    try {
      setLoading(true);
      const data = await Requicicao.Get({
        endpoint: "/Funcionarios/Escala",
        params: { id },
        config: auth.GetHeaders()
      });
      if (data.codigoEstadoHttp === 200) {
        setLista(data.objetoResposta);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const LimpraCampos = () => {
    setMesAno(null);
    setTipoEscala(null);
    setDiasTrabalho([]);
    setHorarioInicio("");
    setHorarioFim("");
    setErrors({});
  };

  const validarCampos = () => {
    const novosErrors = {};

    // Validar Mês e Ano
    if (!mesAno) {
      novosErrors.mesAno = "Mês e Ano é obrigatório";
    }

    // Validar Tipo de Escala
    if (!tipoEscala) {
      novosErrors.tipoEscala = "Tipo de Escala é obrigatório";
    }

    // Validar Dias de Trabalho
    if (!diasTrabalho || diasTrabalho.length === 0) {
      novosErrors.diasTrabalho = "Dias de Trabalho é obrigatório";
    }

    // Validar Horário de Início
    if (!horarioInicio || horarioInicio.trim() === "") {
      novosErrors.horarioInicio = "Horário de Início é obrigatório";
    } else if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(horarioInicio)) {
      novosErrors.horarioInicio = "Horário deve estar no formato HH:MM";
    }

    // Validar Horário de Fim
    if (!horarioFim || horarioFim.trim() === "") {
      novosErrors.horarioFim = "Horário de Fim é obrigatório";
    } else if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(horarioFim)) {
      novosErrors.horarioFim = "Horário deve estar no formato HH:MM";
    }

    // Validar se horário de fim é maior que horário de início
    if (horarioInicio && horarioFim && horarioInicio >= horarioFim) {
      novosErrors.horarioFim = "Horário de Fim deve ser maior que Horário de Início";
    }

    setErrors(novosErrors);
    return Object.keys(novosErrors).length === 0;
  };

  const HandleSalvarEscala = async () => {
    // Validar campos antes de salvar
    if (!validarCampos()) {
      notify({
        type: "aviso",
        message: "Por favor, preencha os campos obrigatórios."
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        mesAno,
        tipoEscala,
        diasTrabalho: diasTrabalho.toString(),
        funcionarioId: id,
        horarioInicio,
        horarioFim
      };

      const data = await Requicicao.Post({
        endpoint: "/Funcionarios/Escala",
        data: payload,
        config: auth.GetHeaders()
      });
      if (data.codigoEstadoHttp === 200) {
        notify({
          type: "success",
          message: "Escala Salva."
        });

        StateDataPage();
        LimpraCampos();
      } else {
        notify({
          type: "Erro",
          message: "Erro ao atribir a nova escala."
        });
      }
    } catch (error) {
      if (error?.response?.status === 400) {
        if (error.response?.data?.title != null)
          notify({
            type: "Erro",
            message: error.response.data.title
          });
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Limpar dias de trabalho quando o tipo de escala mudar
    setDiasTrabalho([]);
    // Limpar erro do campo dias de trabalho
    if (errors.diasTrabalho) {
      setErrors(prev => ({ ...prev, diasTrabalho: undefined }));
    }
  }, [tipoEscala]);

  // Limpar erros quando campos são alterados
  const handleFieldChange = (field, value) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  useEffect(() => {
    if (id != null) StateDataPage();
  }, [id]);

  return (
    <div className="row g-3">


      <div className="col-12 col-md-4">
        <label htmlFor="mesAno">
          <i className="pi pi-calendar me-1" />
          Mês e Ano <span className="text-danger">*</span>
        </label>
        <Calendar
          id="mesAno"
          value={mesAno}
          onChange={(e) => {
            setMesAno(e.value);
            handleFieldChange('mesAno', e.value);
          }}
          view="month"
          dateFormat="mm/yy"
          showButtonBar
          placeholder="Escolha Mês e Ano"
          className={`w-100 ${errors.mesAno ? 'p-invalid' : ''}`}
        />
        {errors.mesAno && (
          <small className="text-danger">{errors.mesAno}</small>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label htmlFor="tipoEscala">
          <i className="pi pi-clock me-1" />
          Tipo de Escala <span className="text-danger">*</span>
        </label>
        <Dropdown
          id="tipoEscala"
          value={tipoEscala}
          options={tipoEscalaOptions}
          onChange={(e) => {
            setTipoEscala(e.value);
            handleFieldChange('tipoEscala', e.value);
          }}
          placeholder="Selecione o Tipo de Escala"
          className={`w-100 ${errors.tipoEscala ? 'p-invalid' : ''}`}
        />
        {errors.tipoEscala && (
          <small className="text-danger">{errors.tipoEscala}</small>
        )}
        {tipoEscala && (
          <small className="text-muted">
            <i className="pi pi-info-circle me-1" />
            Máximo {MaxDiasTrabalho()} dias de trabalho permitidos
          </small>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label htmlFor="diasTrabalho">
          <i className="pi pi-calendar-plus me-1" />
          Dias de Trabalho <span className="text-danger">*</span>
        </label>
        <MultiSelect
          id="diasTrabalho"
          value={diasTrabalho}
          options={diasSemanaOptions}
          onChange={(e) => {
            HandleDiasTrabalhoChange(e);
            handleFieldChange('diasTrabalho', e.value);
          }}
          placeholder={!tipoEscala ? "Primeiro selecione o tipo de escala" : "Selecione os Dias de Trabalho"}
          className={`w-100 ${errors.diasTrabalho ? 'p-invalid' : ''}`}
          disabled={!tipoEscala}
        />
        {errors.diasTrabalho && (
          <small className="text-danger">{errors.diasTrabalho}</small>
        )}
        {diasTrabalho.length > 0 && (
          <small className="text-success">
            <i className="pi pi-check-circle me-1" />
            {diasTrabalho.length} dia(s) selecionado(s)
          </small>
        )}
      </div>

      <div className="col-md-4">
        <label htmlFor="horario-inicio" className="form-label">
          <i className="pi pi-play me-1" />
          Horário de início <span className="text-danger">*</span>
        </label>
        <InputText
          id="horario-inicio"
          name="horario-inicio"
          value={horarioInicio}
          onChange={(e) => {
            const valorComMascara = aplicarMascaraHorario(e.target.value);
            setHorarioInicio(valorComMascara);
            handleFieldChange('horarioInicio', valorComMascara);
          }}
          className={`form-control ${errors.horarioInicio ? 'p-invalid' : ''}`}
          placeholder="Ex: 08:30"
          maxLength={5}
        />
        {errors.horarioInicio && (
          <small className="text-danger">{errors.horarioInicio}</small>
        )}

      </div>

      <div className="col-md-4">
        <label htmlFor="horario-fim" className="form-label">
          <i className="pi pi-calendar-times me-1" />
          Horário de Fim <span className="text-danger">*</span>
        </label>
        <InputText
          id="horario-fim"
          name="horario-fim"
          value={horarioFim}
          onChange={(e) => {
            const valorComMascara = aplicarMascaraHorario(e.target.value);
            setHorarioFim(valorComMascara);
            handleFieldChange('horarioFim', valorComMascara);
          }}
          className={`form-control ${errors.horarioFim ? 'p-invalid' : ''}`}
          placeholder="Ex: 17:30"
          maxLength={5}
        />
        {errors.horarioFim && (
          <small className="text-danger">{errors.horarioFim}</small>
        )}

      </div>

      <div className="col-12">
        <div className="d-flex justify-content-end gap-2">
          <Button
            label="Limpar Campos"
            icon="pi pi-refresh"
            className="btn btn-outline-secondary"
            onClick={LimpraCampos}
          />
          <Button
            label="Salvar Escala"
            icon="pi pi-save"
            className="btn btn-primary"
            onClick={HandleSalvarEscala}
            disabled={loading}
          />
        </div>
      </div>

      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <i className="pi pi-list me-2" />
            Escalas Cadastradas
          </h5>
          <span className="badge bg-primary">{Lista.length} escala(s)</span>
        </div>
        <DataTable value={Lista} stripedRows rows={20} emptyMessage="Nenhuma escala cadastrada">
          <Column
            field="mesAno"
            header="Mês e Ano"
            body={(row) => MaskUtil.applyMesAnoMask(row.mesAno)}
            style={{ width: "15%" }}
          />
          <Column 
            field="tipoEscala" 
            header="Tipo de Escala"
            body={(row) => (
              <span className="badge bg-info">{row.tipoEscala}</span>
            )}
            style={{ width: "15%" }}
          />
          <Column 
            field="horarioInicio" 
            header="Horário de Início"
            body={(row) => (
              <span className="text-success fw-bold">{row.horarioInicio}</span>
            )}
            style={{ width: "15%" }}
          />
          <Column 
            field="horarioFim" 
            header="Horário de Fim"
            body={(row) => (
              <span className="text-danger fw-bold">{row.horarioFim}</span>
            )}
            style={{ width: "15%" }}
          />
          <Column
            field="diasTrabalho"
            header="Dias de Trabalho"
            body={(row) => (
              <span className="badge bg-secondary">
                {row.diasTrabalho.replaceAll(",", ", ")}
              </span>
            )}
            style={{ width: "25%" }}
          />
          <Column
            field="dataCriacao"
            header="Data de Criação"
            body={(row) => MaskUtil.applyDataAndHoraMask(row.dataCriacao)}
            style={{ width: "15%" }}
          />
        </DataTable>
      </div>
    </div>
  );
};

EscalaFuncionario.propTypes = {
  id: PropTypes.string.isRequired
};

export default EscalaFuncionario;
